import React from "react";
import { Link } from "@shopify/polaris";
import { ChevronRight } from "lucide-react";
import { useSelector } from "react-redux";
import { RootState } from "configureStore";
import { useParams, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Category } from "types/Files";

const findParentPath = (
  categories: Category[],
  targetId: string,
  path: Category[] = []
): Category[] | null => {
  for (const category of categories) {
    const currentPath = [...path, category];

    if (category.id === targetId) {
      return currentPath;
    }

    if (category.subcategories) {
      const result = findParentPath(
        category.subcategories,
        targetId,
        currentPath
      );
      if (result) {
        return result;
      }
    }
  }

  return null;
};

const RenderPath: React.FC<{ path: Category[] }> = ({ path }) => {
  const location = useLocation();
  const { companyId, userId } = useParams<{
    companyId: string;
    userId: string;
  }>();
  if (!path || path.length === 0) return null;

  const generateUrl = (catId: string) => {
    if (location.pathname.includes("profile")) {
      return `/app/${companyId}/profile/${userId}/files/${catId}`;
    } else if (location.pathname.includes("documents")) {
      return `/app/${companyId}/documents/${catId}`;
    }
    return "#"; // fallback URL if none of the conditions are met
  };

  return (
    // Each category and the chevron before it form one flex item, so a long
    // path breaks between categories: a chevron never ends a line on its own,
    // and a name wraps inside itself only when it is wider than the whole line.
    <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1">
      {path.map((cat, index) => (
        <span
          key={cat.id}
          className="inline-flex max-w-full items-center gap-x-1.5"
        >
          {index > 0 && (
            <ChevronRight
              size={14}
              aria-hidden="true"
              className="shrink-0 text-gray-500"
            />
          )}
          <Link url={generateUrl(cat.id)}>{cat.title}</Link>
        </span>
      ))}
    </div>
  );
};

const HeaderLine: React.FC<{ catId: string }> = ({ catId }) => {
  const { t } = useTranslation();
  const { categoriesData } = useSelector(
    (state: RootState) => state.documentCategoryReducer
  );

  const pathToCategory = Array.isArray(categoriesData)
    ? findParentPath(categoriesData, catId)
    : [];

  return (
    <div>
      {pathToCategory ? (
        <RenderPath path={pathToCategory} />
      ) : (
        <div>{t("category_not_found")}</div>
      )}
    </div>
  );
};

export default HeaderLine;
